// Built-in libraries
import { useEffect, useMemo, useState } from 'react';
// Third party libraries
import type { ColumnDef } from '@tanstack/react-table';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
// Custom components
import Card from '@/components/UI/Card';
import WidgetLayout from '@/components/Widgets/WidgetLayout';
import { CurrencyFormatDisplay } from '@/components/UI/NumberFormatDisplay/CurrencyFormatDisplay';
import Table from '@/components/UI/Table';
// Services
import { useDashboardData } from '@/hooks/ReactQueryHooks/useDashboardQuery';
import { usePermission } from '@/hooks/usePermission';
import { notifyUser } from '@/store/Toast';
import { convertEnumToText } from '@/utils';
// Assets and Miscellaneous
import type { DashboardType } from '@/types/Analytics.ts';
import { Widget } from '../constants';

interface ProjectSummaryWidgetProps {
  dashboard: DashboardType;
}

// Approximate rendered table section heights (px) for dynamic card sizing.
const TABLE_HEADER_HEIGHT = 56;
const TABLE_ROW_HEIGHT = 57;
const TABLE_FOOTER_HEIGHT = 72;

interface ProjectSummaryRow {
  id: number;
  name: string;
  district?: string | null;
  totalDevelopmentCost: number;
  cityContribution: number;
  status: string;
}

export const getProjectSummaryPageCount = (projectCount: number, perPage: number) => Math.max(1, Math.ceil(projectCount / perPage));

const StatusWidget = ({ dashboard }: ProjectSummaryWidgetProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { hasPermission } = usePermission();
  const [perPage, setPerPage] = useState(10);
  const [page, setPage] = useState(1);

  //const [perPage, setPerPage] = useState<number>(5);
  //const [page, setPage] = useState<number>(1);

  const fetchProjectSummaryWidgetData = useDashboardData({
    dashboard,
    widget: Widget.PROJECTS_SUMMARY_REPORT,
  });

  const projectSummaryWidgetData = fetchProjectSummaryWidgetData?.data?.data;

  const tableData = useMemo(() => (Array.isArray(projectSummaryWidgetData) ? projectSummaryWidgetData : []), [projectSummaryWidgetData]);

  const totalPage = Math.max(1, Math.ceil(tableData.length / perPage));

  const pagedData = useMemo(() => {
    const start = (page - 1) * perPage;
    return tableData.slice(start, start + perPage);
  }, [tableData, page, perPage]);

  useEffect(() => {
    setPage((current) => Math.min(current, totalPage));
  }, [totalPage]);
  //const fetchProjectSummaryWidgetData = useDashboardData({
  //  dashboard,
  //  widget: Widget.PROJECTS_SUMMARY_REPORT,
  //});

  const nextPageHandler = () => {
    setPage((prev) => Math.min(prev + 1, totalPage));
  };
  const projectSummary = useMemo(() => {
    const data = fetchProjectSummaryWidgetData?.data?.data;
    return Array.isArray(data) ? (data as ProjectSummaryRow[]) : [];
  }, [fetchProjectSummaryWidgetData?.data?.data]);
  //const totalPage = getProjectSummaryPageCount(projectSummary.length, perPage);

  const previousPageHandler = () => {
    setPage((prev) => Math.max(prev - 1, 1));
  };
  useEffect(() => {
    setPage((current) => Math.min(current, totalPage));
  }, [totalPage]);

  const _pagedProjectSummary = useMemo(() => {
    const start = (page - 1) * perPage;
    return projectSummary.slice(start, start + perPage);
  }, [page, perPage, projectSummary]);

  const projectDetailViewHandler = (projectId: number) => {
    if (hasPermission(['READ_PROJECT'])) {
      navigate(`${location.pathname.replace('dashboard', 'projects')}/${projectId}/dashboard`);
      return;
    }

    dispatch(
      notifyUser({
        showToast: true,
        toastMessage: 'You do not have permission to read project details',
        toastType: 'info',
      })
    );
  };

  //const tableColumns: ColumnDef<any>[] = [
  const tableColumns: ColumnDef<ProjectSummaryRow>[] = [
    {
      id: 'name',
      header: () => <div className='text-start'>Project Name</div>,
      accessorKey: 'name',
      cell: (props) => {
        const { getValue, row } = props;
        return (
          <button
            type='button'
            className='cursor-pointer text-start hover:underline'
            onClick={() => projectDetailViewHandler(row.original.id)}
          >
            {getValue() as string}
          </button>
        );
      },
    },
    {
      id: 'district',
      header: () => <div className='text-start'>District</div>,
      accessorKey: 'district',
      cell: (props) => <div className='text-start'>{(props.getValue() as string | null | undefined) ?? '—'}</div>,
    },
    {
      id: 'totalDevelopmentCost',
      header: () => <div className='text-end'>Total Development Value</div>,
      accessorKey: 'totalDevelopmentCost',
      cell: (props) => (
        <div className='text-end'>
          <CurrencyFormatDisplay value={props.getValue() as number} prefix='$' />
        </div>
      ),
    },
    {
      id: 'cityContribution',
      header: () => <div className='text-end'>City Contribution</div>,
      accessorKey: 'cityContribution',
      cell: (props) => (
        <div className='text-end'>
          <CurrencyFormatDisplay value={props.getValue() as number} prefix='$' />
        </div>
      ),
    },
    {
      id: 'status',
      header: () => <div className='text-start pl-10'>Status</div>,
      accessorKey: 'status',
      cell: (props) => <div className='text-start pl-10'>{convertEnumToText(props.getValue() as string)}</div>,
    },
  ];

  const tablePagination = {
    page: page - 1,
    pageSize: perPage,
    pageCount: totalPage,
    currentPage: page,
    setPageSize: (v: number) => {
      setPerPage(v);
      setPage(1);
    },
    nextPage: nextPageHandler,
    previousPage: previousPageHandler,
    //nextPage: () => setPage((current) => Math.min(current + 1, totalPage)),
    //previousPage: () => setPage((current) => Math.max(current - 1, 1)),
  };

  const visibleRowCount = pagedData.length;
  const _tableContainerHeight = TABLE_HEADER_HEIGHT + visibleRowCount * TABLE_ROW_HEIGHT + TABLE_FOOTER_HEIGHT;

  return (
    <WidgetLayout dashboard={dashboard} widget={Widget.PROJECT_STATUS_SUMMARY}>
      <Card title='Project Summary'>
        <div className='flex w-full'>
          {tableData.length > 0 && (
            <div className='flex flex-col border rounded-b-md w-full bg-white'>
              <Table cols={tableColumns} tableData={pagedData} pagination={tablePagination} />
            </div>
          )}
        </div>
      </Card>
    </WidgetLayout>
  );
};

export default StatusWidget;
